import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import {
    AjaxListingContext,
    AjaxListingNextButton
} from '$partials/base/ajax-listing.jsx';

import { Columns } from '$partials/layout/columns.jsx';
import { Dispatcher as ItemDispatcher } from '$partials/listings';
import { useAjaxListing } from '@hooks/ajax-listing';
import { useLanguage } from '$contexts/language.jsx';
import { StaticTitle } from '$partials/layout/side-titles.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GridMixedAjax = ({
    ajaxPath  = null,
    items     = null,
    sideTitle = null,
    title     = null,
    urlPath   = null,
}) => {
    // references
    const contentRef = useRef();

    // hooks
    const listing = useAjaxListing({
        infinite: true,
        items: items?.edges,
        page: items?.pageInfo?.page || 1,
        path: ajaxPath || `/api/v1/listings/${encodeURIComponent(urlPath)}`,
        query: {
            tags: items?.filters?.tags || null,
            term: items?.filters?.term || null
        },
        totalItems: items?.total,
        totalPages: items?.totalPages
    });

    const { translate: t } = useLanguage();

    // render
    return (
        <section className='module-grid-mixed-ajax'>
            {title && (
                <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                    <div className='flex flex-wrap mb6 sm-mb8 md-mb9'>
                        {/* side title */}
                        <div className='absolute top-0 md-relative md-col-2'>
                            {sideTitle && (
                                <div className='px-gutter'>
                                    <p className='type-style-5 side-title'>
                                        <StaticTitle title={sideTitle} />
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* content / title */}
                        <div className='col-11 sm-col-8 md-col-7'>
                            <div className='ml9 sm-ml9 md-m0'>
                                <div className='px-gutter'>
                                    <div
                                        className='rich-text type-style-1'
                                        dangerouslySetInnerHTML={{__html: title}} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* content */}
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2'>
                        {(!title && sideTitle) && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title side-title--no-top'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* listing */}
                    <AjaxListingContext.Provider value={listing}>
                        <div
                            className='col-12 sm-col-12 md-col-10'
                            ref={contentRef}>
                            <div className='ml9 sm-ml9 md-ml0'>
                                <section
                                    aria-busy={listing.loading}
                                    aria-label='Tagged items'
                                    role='feed'>
                                    {/* mobile */}
                                    <div className='sm-hide'>
                                        {listing.items?.map((edge) => {
                                            const item = edge?.node;

                                            return (
                                                <React.Fragment key={item?.id}>
                                                    {item && (
                                                        <article
                                                            aria-label={item.title}
                                                            aria-posinset={item.originalIndex}
                                                            aria-setsize={listing.totalItems}
                                                            className='col-12'
                                                            tabIndex={-1}>
                                                            <div className={`px-gutter${item.originalIndex === 0 ? '' : ' pt-gutter'}`}>
                                                                <ItemDispatcher
                                                                    containerTag='div'
                                                                    item={item} />
                                                            </div>
                                                        </article>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>

                                    {/* tablet / desktop */}
                                    <div className='sm-show'>
                                        <div className='flex flex-wrap'>
                                            <Columns
                                                count={2}
                                                items={listing.items?.map((edge, idx) => {
                                                    const item = edge?.node;

                                                    return (
                                                        <React.Fragment key={item?.originalIndex}>
                                                            {item && (
                                                                <article
                                                                    aria-label={item.title}
                                                                    aria-posinset={item.originalIndex}
                                                                    aria-setsize={listing.totalItems}
                                                                    tabIndex={-1}>
                                                                    <div className={`px-gutter${item.originalIndex < 2 ? '' : ' pt-gutter'}`}>
                                                                        <ItemDispatcher
                                                                            alternate={(idx % 2 === 0)}
                                                                            item={item}
                                                                            key={item.originalIndex} />
                                                                    </div>
                                                                </article>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })} />
                                        </div>
                                    </div>

                                    {/* controls */}
                                    {(listing.page !== listing.totalPages) && (
                                        <div className='my7'>
                                            <div className='flex justify-center items-center'>
                                                <AjaxListingNextButton className='form-field-reset button button--loading'>
                                                    <span className='capitalise'>
                                                        {t('See More')}
                                                    </span>
                                                </AjaxListingNextButton>
                                            </div>
                                        </div>
                                    )}
                                </section>
                            </div>
                        </div>
                    </AjaxListingContext.Provider>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    ajaxPath:  PropTypes.string,
    items:     PropTypes.object.isRequired,
    sideTitle: PropTypes.string,
    title:     PropTypes.string,
    urlPath:   PropTypes.string,
};

GridMixedAjax.propTypes = propTypes;

export { GridMixedAjax };

export default GridMixedAjax;
